import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { getToken } from '../../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

interface RevokeDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  document: { id: string; verification_id: string } | null;
  onSuccess: () => void;
}

export function RevokeDocumentDialog({ open, onClose, document, onSuccess }: RevokeDocumentDialogProps) {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<string>('issued_in_error');
  const [reasonText, setReasonText] = useState('');

  const handleRevoke = async () => {
    if (!document) return;
    if (reason === 'other' && !reasonText.trim()) {
      notify('Please provide a reason', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${PAPERMAN_API_URL}documents/${document.id}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason,
          reason_text: reason === 'other' ? reasonText : undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to revoke');
      }

      notify('Document revoked', { type: 'success' });
      onSuccess();
      onClose();
    } catch (e: any) {
      notify(e.message || 'Failed to revoke document', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Revoke Document</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
          <Alert severity="warning" sx={{ fontSize: '12px' }}>
            This will permanently revoke <strong>{document?.verification_id}</strong>. The verification page will immediately show this document as revoked. This action cannot be undone.
          </Alert>

          <TextField
            select
            label="Reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
            fullWidth
            required
          >
            <MenuItem value="issued_in_error">Issued in error</MenuItem>
            <MenuItem value="fraudulent_request">Fraudulent request</MenuItem>
            <MenuItem value="compliance_issue">Compliance issue</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {reason === 'other' && (
            <TextField
              label="Reason details"
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleRevoke} variant="contained" color="error" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Revoke'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
